import { Box, MenuItem, SelectChangeEvent, TextField } from '@mui/material'
import React, { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import TextButton, { TextButtonVariant } from '../Buttons/TextButton'
import DropdownSelectOptions from '../Interfaces/DropdownSelectOptions'
import { ProgramTutors } from '../../swagger'

export interface TutorFormFieldProps {
  tutors: DropdownSelectOptions[]
  isDisabled: boolean
  tutorFormKey: string
  tutorFormId: string
  removeTutorField: (fieldId: string, tutorFieldKey: string) => void
  tutorName: string
  onTutorChange: (tutorKey: number, event: SelectChangeEvent<unknown>) => void
  error?: boolean
  programTutors?: Array<ProgramTutors>
  onClick?: () => void
}

const TutorFormField: React.FC<TutorFormFieldProps> = (props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { error = false } = props

  const onTutorChange = (
    event: SelectChangeEvent<unknown>,
    child: ReactNode
  ) => {
    const actorKey: number = +((child as ReactElement).key
      ?.toString()
      .slice(2) as string)

    props.onTutorChange(actorKey, event)
  }

  const validationMessage = t(
    'Programs.ProgramForm.ValidationMessage.Tutor',
    'Please select a tutor.'
  )

  return (
    <>
      <Box display="flex">
        <Box
          width={props.isDisabled ? '100%' : '75%'}
          onClick={
            props.isDisabled && props.tutorName && !props.onClick
              ? () => {
                  const selectedTutor = props.tutors.find(
                    (tutor) => tutor.label === props.tutorName
                  )

                  // Find the corresponding program tutor to get userKey
                  const programTutor = props.programTutors?.find(
                    (pTutor) => pTutor.actorKey === selectedTutor?.id
                  )

                  if (programTutor?.userKey) {
                    navigate(`/admin/user-accounts/${programTutor.userKey}`)
                  }
                }
              : undefined
          }
          sx={{
            cursor: props.isDisabled && props.tutorName ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor:
                props.isDisabled && props.tutorName
                  ? 'action.hover'
                  : 'transparent',
            },
            position: 'relative',
          }}
        >
          <TextField
            variant="filled"
            select={!props.isDisabled && !!props.tutors}
            disabled={props.isDisabled}
            label={t('Programs.ProgramFormCard.FormField.NewTutor', 'Tutor')}
            key={props.tutorFormKey}
            id={props.tutorFormId}
            SelectProps={{ onChange: onTutorChange }}
            fullWidth
            value={props.tutorName}
            name={props.tutorFormKey}
            helperText={error ? validationMessage : null}
            error={error}
            sx={{
              pointerEvents: props.isDisabled ? 'none' : 'auto',
            }}
          >
            {props.tutors.map((tutor) => (
              <MenuItem
                key={tutor.id}
                value={tutor.label}
                disabled={tutor.disabled}
                style={{
                  textDecoration: tutor.disabled ? 'line-through' : 'none',
                }}
              >
                {tutor.label}
              </MenuItem>
            ))}
          </TextField>
          {props.isDisabled && props.onClick && (
            <Box
              data-testid="tutor-form-field-overlay"
              onClick={(e) => {
                e.stopPropagation()
                props.onClick?.()
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                cursor: 'pointer',
                zIndex: 1,
              }}
            />
          )}
        </Box>
        {!props.isDisabled && (
          <Box width="25%" alignItems="center" justifyContent="flex-end">
            <TextButton
              id={`removeButton-${props.tutorFormKey}`}
              variant={TextButtonVariant.Remove}
              onClick={() =>
                props.removeTutorField(props.tutorFormId, props.tutorFormKey)
              }
              fullWidth
            />
          </Box>
        )}
      </Box>
    </>
  )
}

export default TutorFormField
